import {
    AuthContext,
    DataTableCell,
    FilterMethod,
    IFilterSchema,
    IColumnFiltersConfig,
    ISelectOption,
    ITableColumn,
    ITableFetchOptions,
    InfiniteScrollDataTable,
    InfiniteScrollDataTablePresenter,
    ListProviderPayerProvidersOrderEnum,
    ProviderPayerProvider,
    ProviderPayerProviderSortType,
    Settings,
    TableExport,
    renderAddressGroup,
    renderString,
} from '@enter/core';
import Icon from '@mdi/react';
import { PageHeader, PageLayout, PageLayoutRow } from '../../../components';
import { inject, observer } from 'mobx-react';
import _ from 'lodash';
import React, { useContext, useMemo, useState } from 'react';
import { IInjectedRootStore, IItemSortOrder, parseSortBy } from '../../../stores';
import * as S from './payers-providers.styles';

const getProviderName = (row: ProviderPayerProvider): string => {
    const provider = row.provider;
    if (!provider) {
        return '';
    }
    if (provider.organization) {
        return provider.organization;
    }
    return [provider.first, provider.last].filter(Boolean).join(' ');
};

const payersProvidersColumns: ITableColumn<ProviderPayerProvider, ProviderPayerProviderSortType>[] = [
    {
        id: 'Id',
        Header: 'ID',
        accessor: 'id',
        Cell: DataTableCell('String') as any,
        disableSortBy: true,
        show: false,
    },
    {
        id: 'Contract',
        Header: 'Contract',
        accessor: 'contract',
        Cell: DataTableCell('String') as any,
        disableSortBy: true,
    },
    {
        id: 'Payer',
        Header: 'Payer',
        accessor: (row): string => row.payer?.name || '',
        Cell: (props): string | React.ReactElement => {
            return DataTableCell('AsyncList', {
                type: 'AsyncList',
                loadMethod: async (): Promise<React.ReactElement> => {
                    const payer = props.row.original.payer;
                    return (
                        <S.InfoPanel>
                            <S.InfoTitle>Payer Details</S.InfoTitle>
                            <S.InfoRow>
                                <S.InfoLabel>ID</S.InfoLabel>
                                <S.InfoValue>{renderString(payer?.id)}</S.InfoValue>
                            </S.InfoRow>
                            <S.InfoRow>
                                <S.InfoLabel>Name</S.InfoLabel>
                                <S.InfoValue>{renderString(payer?.name)}</S.InfoValue>
                            </S.InfoRow>
                            <S.InfoRow>
                                <S.InfoLabel>Nickname</S.InfoLabel>
                                <S.InfoValue>{renderString(payer?.nickname)}</S.InfoValue>
                            </S.InfoRow>
                            <S.InfoRow>
                                <S.InfoLabel>Coverage</S.InfoLabel>
                                <S.InfoValue>{renderString(payer?.coverage)}</S.InfoValue>
                            </S.InfoRow>
                            <S.InfoRow>
                                <S.InfoLabel>Email</S.InfoLabel>
                                <S.InfoValue>
                                    {renderString(payer?.contact?.emailClaim || payer?.contact?.emailOther)}
                                </S.InfoValue>
                            </S.InfoRow>
                            <S.InfoRow>
                                <S.InfoLabel>Phone</S.InfoLabel>
                                <S.InfoValue>
                                    {renderString(payer?.contact?.phoneClaim || payer?.contact?.phoneOther)}
                                </S.InfoValue>
                            </S.InfoRow>
                            <S.InfoRow>
                                <S.InfoLabel>Address</S.InfoLabel>
                                <S.InfoValue>
                                    {renderAddressGroup(
                                        _.compact([payer?.contact?.addressClaim, payer?.contact?.addressOther]),
                                    )}
                                </S.InfoValue>
                            </S.InfoRow>
                        </S.InfoPanel>
                    );
                },
            })(props);
        },
        disableSortBy: true,
    },
    {
        id: 'Provider',
        Header: 'Provider',
        accessor: (row): string => getProviderName(row),
        Cell: (props): string | React.ReactElement => {
            return DataTableCell('AsyncList', {
                type: 'AsyncList',
                loadMethod: async (): Promise<React.ReactElement> => {
                    const provider = props.row.original.provider;
                    return (
                        <S.InfoPanel>
                            <S.InfoTitle>Provider Details</S.InfoTitle>
                            <S.InfoRow>
                                <S.InfoLabel>ID</S.InfoLabel>
                                <S.InfoValue>{renderString(provider?.id)}</S.InfoValue>
                            </S.InfoRow>
                            <S.InfoRow>
                                <S.InfoLabel>Organization</S.InfoLabel>
                                <S.InfoValue>{renderString(provider?.organization)}</S.InfoValue>
                            </S.InfoRow>
                            <S.InfoRow>
                                <S.InfoLabel>Name</S.InfoLabel>
                                <S.InfoValue>{renderString(getProviderName(props.row.original))}</S.InfoValue>
                            </S.InfoRow>
                            <S.InfoRow>
                                <S.InfoLabel>NPI</S.InfoLabel>
                                <S.InfoValue>{renderString(provider?.npi)}</S.InfoValue>
                            </S.InfoRow>
                            <S.InfoRow>
                                <S.InfoLabel>Email</S.InfoLabel>
                                <S.InfoValue>{renderString(provider?.email)}</S.InfoValue>
                            </S.InfoRow>
                            <S.InfoRow>
                                <S.InfoLabel>Phone</S.InfoLabel>
                                <S.InfoValue>{renderString(provider?.phone)}</S.InfoValue>
                            </S.InfoRow>
                        </S.InfoPanel>
                    );
                },
            })(props);
        },
        disableSortBy: true,
    },
    {
        id: 'Halt',
        Header: 'Halt',
        accessor: 'halt',
        Cell: DataTableCell('Boolean') as any,
        disableSortBy: true,
    },
    {
        id: 'Created',
        Header: 'Created',
        accessor: 'created',
        Cell: DataTableCell('DateTime') as any,
        sortType: ProviderPayerProviderSortType.CREATED,
    },
];

const payersProvidersFilterSchemas: IFilterSchema[] = [
    {
        label: 'Payer',
        filterId: 'filterPayerId',
        filterType: 'AsyncSelect',
        columnId: 'Payer',
        loadOptions: async (): Promise<ISelectOption[]> => [],
    },
    {
        label: 'Provider',
        filterId: 'filterProviderId',
        filterType: 'AsyncSelect',
        columnId: 'Provider',
        loadOptions: async (): Promise<ISelectOption[]> => [],
    },
    {
        label: 'Created',
        filterId: 'filterCreated',
        filterType: 'DateTime',
        columnId: 'Created',
    },
];

const payersProvidersColumnFiltersConfig: IColumnFiltersConfig = {
    Payer: {
        filterType: 'AsyncSelect',
        filterId: 'filterPayerId',
        label: 'Filter by Payer',
        defaultMethod: FilterMethod.EQ,
        loadOptions: async (): Promise<ISelectOption[]> => [],
    },
    Provider: {
        filterType: 'AsyncSelect',
        filterId: 'filterProviderId',
        label: 'Filter by Provider',
        defaultMethod: FilterMethod.EQ,
        loadOptions: async (): Promise<ISelectOption[]> => [],
    },
    Created: {
        filterType: 'DateTime',
        filterId: 'filterCreated',
        label: 'Filter by Created Date',
        defaultMethod: FilterMethod.GTE,
    },
};

type PayersProps = IInjectedRootStore;

export const PayersProviders = inject('rootStore')(
    observer((props: PayersProps) => {
        const { rootStore } = props;
        const payersStore = rootStore?.payersStore;
        const authContext = useContext(AuthContext);
        const [loading, setLoading] = useState(false);
        const [lastFetchOptions, setLastFetchOptions] = useState<ITableFetchOptions | undefined>(undefined);

        const presenter = useMemo(
            () =>
                new InfiniteScrollDataTablePresenter<ProviderPayerProvider>({
                    name: 'provider-payers',
                    data: [],
                    columns: payersProvidersColumns,
                    filterSchemas: payersProvidersFilterSchemas,
                }),
            [],
        );

        React.useEffect(() => {
            document.title = 'ENTER Platform - Payers Providers';
            return () => authContext.cancelGroup?.('providerPayers');
        }, [authContext]);

        const loadPayerOptions = React.useCallback(
            async (searchTerm: string): Promise<ISelectOption[]> => {
                if (payersStore && !payersStore.filterOptionsLoaded) {
                    await payersStore.loadFilterOptionsOnce();
                }
                const options = payersStore?.payerFilterOptions || [];
                const term = (searchTerm || '').toLowerCase().trim();
                if (!term) {
                    return options;
                }
                return options.filter(option =>
                    String(option.label || '')
                        .toLowerCase()
                        .includes(term),
                );
            },
            [payersStore],
        );

        const loadProviderOptions = React.useCallback(
            async (searchTerm: string): Promise<ISelectOption[]> => {
                if (payersStore && !payersStore.filterOptionsLoaded) {
                    await payersStore.loadFilterOptionsOnce();
                }
                const options = payersStore?.providerFilterOptions || [];
                const term = (searchTerm || '').toLowerCase().trim();
                if (!term) {
                    return options;
                }
                return options.filter(option =>
                    String(option.label || '')
                        .toLowerCase()
                        .includes(term),
                );
            },
            [payersStore],
        );

        const columnFiltersConfig = React.useMemo<IColumnFiltersConfig>(
            () => ({
                ...payersProvidersColumnFiltersConfig,
                Payer: {
                    ...payersProvidersColumnFiltersConfig.Payer,
                    loadOptions: loadPayerOptions,
                },
                Provider: {
                    ...payersProvidersColumnFiltersConfig.Provider,
                    loadOptions: loadProviderOptions,
                },
            }),
            [loadPayerOptions, loadProviderOptions],
        );

        const onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
            if (!payersStore) {
                return;
            }
            const { pageIndex, pageSize, sortBy, validFilters } = fetchOptions;
            setLastFetchOptions(fetchOptions);
            setLoading(true);
            try {
                if (pageIndex === 0) {
                    presenter.resetData();
                }
                authContext.cancelGroup?.('providerPayers');
                authContext.setGroup?.('providerPayers');

                const { sort, order } = parseSortBy(sortBy, payersProvidersColumns);
                const sortEnums = (sort as (ProviderPayerProviderSortType | undefined)[] | undefined)?.filter(
                    (value): value is ProviderPayerProviderSortType => Boolean(value),
                );
                const orderEnums = order?.map(value =>
                    value === IItemSortOrder.ASCENDING
                        ? ListProviderPayerProvidersOrderEnum.ASCENDING
                        : ListProviderPayerProvidersOrderEnum.DESCENDING,
                );

                const listing = await payersStore.loadPayersListing({
                    page: pageIndex,
                    pageSize,
                    sort: sortEnums,
                    order: orderEnums,
                    filters: validFilters,
                });

                presenter.onDataUpdate(listing.payers);
                presenter.onDataTotalChange(listing.total ?? listing.payers.length);
                presenter.setHasMore(presenter.accumulatedData.length < (listing.total ?? 0));
            } catch (error) {
                console.error('Failed to load provider payers listing', error);
                presenter.setIsLoadingMore(false);
            } finally {
                setLoading(false);
            }
        };

        const renderToolbar = (rows: any[], prepareRow: (row: any) => void): React.ReactElement => {
            return (
                <S.TableHeaderSection>
                    <S.ToolbarActions>
                        <Settings
                            columns={payersProvidersColumns}
                            hiddenColumnIds={presenter.hiddenColumnIds}
                            onColumnToggle={presenter.onColumnToggle}
                            pageSize={presenter.controlledPageSize}
                            onPageSizeChange={presenter.onPageSizeUpdate}
                            pageSizeOptions={[25, 50, 100]}
                            variant="small"
                        />
                        <TableExport rows={rows} prepareRow={prepareRow} />
                    </S.ToolbarActions>
                </S.TableHeaderSection>
            );
        };

        if (!rootStore || !payersStore) {
            return <></>;
        }

        return (
            <PageLayout>
                <PageHeader title="Payers Providers" hideDateRange={true}>
                    <S.HeaderActions />
                </PageHeader>
                <PageLayoutRow expand={true}>
                    <S.ListContainer>
                        <S.TableCard>
                            <S.TableWrapper>
                                <S.PayersTable
                                    presenter={presenter}
                                    isLoading={loading}
                                    onFetch={onFetch}
                                    renderToolbar={renderToolbar}
                                    columnFiltersConfig={columnFiltersConfig}
                                    hideFiltering={true}
                                    hideNavigation={true}
                                    hideSettings={true}
                                    hideSelectAll={true}
                                    showExport={false}
                                    pageSizeOptions={[25, 50, 100]}
                                />
                            </S.TableWrapper>
                        </S.TableCard>
                    </S.ListContainer>
                </PageLayoutRow>
            </PageLayout>
        );
    }),
);
